import { rd, RemoteData } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import { ThunkDispatch, unwrapResult } from '@reduxjs/toolkit';
import { IntegrationID, type Group, type Resource } from '__types__';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AnyAction } from 'redux';
import { selectIntegrationById } from 'store/integrations';
import { getNetSuiteMasterProjectsRequest } from 'store/netsuite';
import type { RootStore } from 'store/reducer';
import {
  renderResourceGroupOption,
  createResourceGroupValueRenderer
} from 'v5/features/resources/resourceGroupOptions';
import {
  agentDetailsOf,
  isAgentRef,
  agentPickUpdates
} from 'v5/core/entity-values/codecs/entity-ref.agent';
import type { ResourceFormKitConfig } from 'v5/patterns/forms/schema-form/fields/resourceFormKit';
import type {
  NetsuiteMasterProject,
  NetsuiteMasterProjectsState
} from 'v5/patterns/forms/schema-form/fields/NetsuiteMasterProjectField';
import type { SchemaFormHandle } from 'v5/patterns/forms/schema-form/schemaForm.types';
import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import { WithServices } from 'v5/platform/ts/services';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { toCountryCode } from 'v5/patterns/widgets/PhoneInput/country';
import { WithGeolocationService } from 'v5/services/io/GeolocationService/GeolocationService';
import { resourceDisplayName } from 'v5/patterns/resource/resourceDisplayName';
import {
  createResourceReferenceSource,
  type ResourceReferenceServices
} from 'v5/patterns/resource/resourceReferenceSource';

export type ResourceFormKitServices = ResourceReferenceServices &
  WithServices<
    [
      WithGeolocationService,
      /** The browsing workspace's country, for new phone numbers. */
      WithSystemSettingsService
    ]
  >;

/** The v1 thunk store is untyped; this is the dispatch the NetSuite thunk needs. */
type ThunkStoreDispatch = ThunkDispatch<RootStore, undefined, AnyAction>;

/** `GET /integrations/splashlight/netsuite_master_projects` rows, parsed defensively. */
function parseMasterProjects(data: unknown): NetsuiteMasterProject[] {
  if (!Array.isArray(data)) return [];
  const projects: NetsuiteMasterProject[] = [];
  for (const row of data) {
    if (typeof row !== 'object' || row === null) continue;
    const record: Record<string, unknown> = row;
    const id = Number(record.id);
    if (!Number.isInteger(id)) continue;
    projects.push({
      id,
      name: typeof record.name === 'string' ? record.name : String(id)
    });
  }
  return projects.sort((a, b) => a.name.localeCompare(b.name));
}

function toError(cause: unknown): Error {
  if (cause instanceof Error) return cause;
  const message =
    typeof cause === 'object' &&
    cause !== null &&
    'message' in cause &&
    typeof cause.message === 'string'
      ? cause.message
      : 'Failed to load NetSuite master projects';
  return new Error(message);
}

/**
 * NetSuite master projects the same way the legacy
 * `SplashlightNetsuiteMasterProjectSelector` read them: the integration flag
 * from the v1 store, the list through the `store/netsuite` thunk. Redux stays
 * here in the composition layer; the engine only sees `RemoteData`.
 */
function useNetsuiteMasterProjects(): RemoteData<NetsuiteMasterProjectsState> {
  const dispatch = useDispatch<ThunkStoreDispatch>();
  const integration = useSelector((state: RootStore) =>
    selectIntegrationById(state, IntegrationID.NETSUITE)
  );
  const enabled = Boolean(integration?.enabled);
  const [projects, setProjects] = useState<RemoteData<NetsuiteMasterProject[]>>(
    rd.ofPending()
  );

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setProjects(rd.ofPending());
    dispatch(getNetSuiteMasterProjectsRequest())
      .then(unwrapResult)
      .then(response => {
        if (cancelled) return;
        setProjects(rd.of(parseMasterProjects(response?.data)));
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProjects(rd.ofError(toError(cause)));
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, enabled]);

  return useMemo(
    () =>
      enabled
        ? rd.map(projects, list => ({
            type: 'enabled' as const,
            projects: list
          }))
        : rd.of<NetsuiteMasterProjectsState>({ type: 'disabled' }),
    [enabled, projects]
  );
}

/**
 * What the resource kit needs from the app: the service-bound fields
 * (location search, agent / organisation lookup and links, an agent's
 * details copied on pick, group badges).
 *
 * The kit's components are built from it, so it must keep its identity
 * across renders: a new config means a new component type for every
 * service-bound field, and React then unmounts and remounts those fields
 * (losing focus, ghost rows and typed text). It is therefore memoised on
 * `services` only; the group lists and the browsing scope are read through a
 * ref at render time.
 *
 * @param createForm the create form's handle: an agent picked there fills
 *   the resource's details at once (edit mode copies them on save instead).
 */
export function useResourceFormKitConfig(
  services: ResourceFormKitServices['services'],
  groups: Group[],
  selectedGroups: Group[],
  /** The resource edited (`null` while creating): its agent and organisation come with it. */
  resource: Resource | null,
  createForm?: RefObject<SchemaFormHandle<ResourceKinds> | null>
): ResourceFormKitConfig {
  const appScope = services.appScopeService.useSlugAppScope();
  const latest = useRef({ groups, selectedGroups, resource, appScope });
  latest.current = { groups, selectedGroups, resource, appScope };
  return useMemo(() => {
    const source = createResourceReferenceSource(
      services,
      () => latest.current.appScope
    );
    return {
      contactDetails: {
        // As legacy `PHONE_NUMBERS`: new phones read in the browsing workspace's country.
        useDefaultCountry: () =>
          toCountryCode(
            rd.tryGet(
              services.systemSettingsService.useSetting(
                services.appScopeService.useResolvedScopeIdentity(),
                'defaults_country_code'
              )
            )?.value
          ) ?? null
      },
      locations: {
        useLocations: query => services.geolocationService.useLocations(query)
      },
      entityRef: {
        resource: {
          ...source,
          nameOf: id => embeddedResourceName(latest.current.resource, id),
          useOptions: (query, { resourceTypeModels: models }) =>
            source.useOptions(query, {
              resource_type_model: { operator: 'matchAny', value: models }
            }),
          useCreate: ({ resourceTypeModels: models }) =>
            source.useCreate(
              type => !!type.model && models.includes(type.model)
            ),
          // Edit mode copies on save instead (`resourceBindings`).
          onPick: ({ spec, id }) => {
            if (!isAgentRef(spec) || createForm === undefined) return;
            services.resourceService
              .ensureItem(id)
              .then(agent => {
                createForm.current?.updateFields(
                  agentPickUpdates(agentDetailsOf(agent))
                );
              })
              .catch((cause: unknown) =>
                services.toastService.open({
                  type: 'error',
                  message: ensureError(cause).message
                })
              );
          }
        }
      },
      groups: {
        renderSelectedValue: props =>
          createResourceGroupValueRenderer(
            latest.current.groups,
            latest.current.selectedGroups
          )(props),
        renderOption: renderResourceGroupOption
      },
      netsuite: { useMasterProjects: useNetsuiteMasterProjects }
    } satisfies ResourceFormKitConfig;
  }, [services, createForm]);
}

/** The name of the agent or organisation `id` that the resource's own payload carries; `null` for another one. */
export function embeddedResourceName(
  resource: Pick<Resource, 'organisation' | 'represented_by'> | null,
  id: number
): string | null {
  const named = [resource?.organisation, resource?.represented_by].find(
    ref => ref?.id === id
  );
  return named ? resourceDisplayName(named) : null;
}
